import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { askAssistant, emailAssistantAnswer, getAssistantThread, type AssistantMessage } from '@/data/client';
import { AssistantChart } from './AssistantChart';

/* The conversation itself: messages, the status line while tools run, the
   chart and the handoff card on an answer, and the composer. One component,
   two homes — the slide-over drawer and the full page. */

const SUGGESTIONS: Record<string, string[]> = {
  '/': ['How did this month compare to last month?', 'Where are my leads coming from?', 'What stands out right now?'],
  '/leads': ['How many leads came in this month, by source?', 'Which leads are still unworked?', 'What is my average lead quality score?'],
  '/traffic': ['Which pages brought the most visitors?', 'How much traffic comes from AI assistants?', 'What are my top search queries?'],
  '/campaigns': ['What is my cost per lead by campaign?', 'Which campaign produced the most sold jobs?', 'How does spend compare to last period?'],
  '/reviews': ['What is my average rating this quarter?', 'What do recent reviews praise most?', 'How quickly are reviews being answered?'],
  '/content': ['What content is in progress right now?', 'What was published in the last 90 days?', 'How do I request an interview?'],
  '/conversation': ['How does the website chat work?', 'How do I add an FAQ answer?', 'Where do chat leads show up?'],
};
function suggestionsFor(pathname: string): string[] {
  const key = Object.keys(SUGGESTIONS).filter((k) => k !== '/').find((k) => pathname.startsWith(k));
  return SUGGESTIONS[key ?? '/'];
}

export function AssistantPanel({ threadId, onThread, compact = false, onNavigate }: { threadId: string | null; onThread: (id: string) => void; compact?: boolean; onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const { user } = useUser();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['assistant', 'thread', threadId], queryFn: () => getAssistantThread(threadId!), enabled: !!threadId });
  const [pending, setPending] = useState<{ question: string; status: string } | null>(null);
  const [live, setLive] = useState<AssistantMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const messages = [...(data?.messages ?? []), ...live];

  useEffect(() => { setLive([]); }, [threadId]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages.length, pending?.status]);

  const ask = async (q: string) => {
    const question = q.trim();
    if (!question || pending) return;
    setDraft(''); setErr(null);
    setPending({ question, status: 'Thinking…' });
    const userMsg: AssistantMessage = { id: `local-${Date.now()}`, role: 'user', text: question, meta: null, createdAt: new Date().toISOString() };
    setLive((l) => [...l, userMsg]);
    try {
      let tid = threadId;
      await askAssistant(threadId ?? 'new', question, user?.fullName ?? '', (ev, d) => {
        if (ev === 'thread') { tid = (d as { id: string }).id; if (!threadId) onThread(tid); }
        else if (ev === 'status') setPending((p) => (p ? { ...p, status: (d as { text: string }).text } : p));
        else if (ev === 'answer') {
          const a = d as { id: string; text: string; meta: AssistantMessage['meta'] };
          setLive((l) => [...l, { id: a.id, role: 'assistant', text: a.text, meta: a.meta, createdAt: new Date().toISOString() }]);
        }
      });
      qc.invalidateQueries({ queryKey: ['assistant', 'threads'] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'The assistant could not answer.');
    } finally {
      setPending(null);
    }
  };

  return (
    <div className={`asst ${compact ? 'asst--compact' : ''}`}>
      <div className="asst__scroll">
        {messages.length === 0 && !pending && (
          <div className="asst__welcome">
            <span className="asst__welcome-icon"><Icon name="sparkles" size={22} /></span>
            <h3>Ask tritonIQ</h3>
            <p>Questions about this account's leads, traffic, campaigns, reviews and content, answered from the same data the reports show. For what to do next, it will point you to your Triton team.</p>
            <div className="asst__suggest">
              {suggestionsFor(pathname).map((s) => <button key={s} type="button" onClick={() => ask(s)}>{s}</button>)}
            </div>
            <p className="asst__notice">Conversations are visible to your Triton team.</p>
          </div>
        )}
        {messages.map((m) => <MessageView key={m.id} m={m} threadId={threadId} onNavigate={onNavigate} />)}
        {pending && (
          <div className="asst__msg asst__msg--assistant asst__msg--pending">
            <span className="asst__spinner" aria-hidden="true" />
            <span>{pending.status}</span>
          </div>
        )}
        {err && <p className="asst__err">{err}</p>}
        <div ref={endRef} />
      </div>
      <form className="asst__composer" onSubmit={(e) => { e.preventDefault(); void ask(draft); }}>
        <textarea
          className="asst__input" rows={compact ? 2 : 3} value={draft} placeholder="Ask about this account…" disabled={!!pending}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void ask(draft); } }}
        />
        <Button type="submit" size="sm" disabled={!!pending || !draft.trim()} iconLeft={<Icon name="send" size={14} />}>Ask</Button>
      </form>
    </div>
  );
}

function MessageView({ m, threadId, onNavigate }: { m: AssistantMessage; threadId: string | null; onNavigate?: () => void }) {
  const { user } = useUser();
  const [mailed, setMailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const handoff = m.meta?.handoff ?? null;
  const email = async () => {
    if (!threadId) return;
    setBusy(true);
    try {
      const r = await emailAssistantAnswer(threadId, m.id, { fromName: user?.fullName ?? '', fromEmail: user?.primaryEmailAddress?.emailAddress ?? '' });
      setMailed(`Sent to ${r.to}.`);
    } catch (e) { setMailed(e instanceof Error ? e.message.replace(/^API \d+: /, '') : 'Could not send.'); }
    finally { setBusy(false); }
  };
  return (
    <div className={`asst__msg asst__msg--${m.role}`}>
      <div className="asst__bubble">
        {m.text.split(/\n{2,}/).map((p, i) => <p key={i}>{renderInline(p, onNavigate)}</p>)}
      </div>
      {m.meta?.chart && <AssistantChart spec={m.meta.chart} />}
      {handoff && (
        <div className="asst__handoff">
          <Icon name="users" size={16} />
          <div>
            <strong>{handoff.contact ? `A question for ${handoff.contact.name}, your ${handoff.contact.role.toLowerCase()}.` : 'A question for your Triton team.'}</strong>
            <span>Recommendations come from the people who run your marketing, not from the assistant.</span>
            {handoff.contact && threadId && (
              mailed ? <em>{mailed}</em> : <Button size="sm" variant="outline" disabled={busy} onClick={email} iconLeft={<Icon name="mail" size={14} />}>{busy ? 'Sending…' : `Email ${handoff.contact.name.split(' ')[0]}`}</Button>
            )}
          </div>
        </div>
      )}
      {m.role === 'assistant' && m.meta && m.meta.sources.length > 0 && (
        <div className="asst__sources">Sources: {m.meta.sources.join(' · ')}</div>
      )}
    </div>
  );
}

/* Just enough formatting for an answer: bold, [links](/path) into the app or
   out to the web, and lines starting with "- " as a list. Anything richer
   belongs in a chart or a table, not in prose. */
function renderInline(p: string, onNavigate?: () => void) {
  const lines = p.split('\n');
  if (lines.length > 1 && lines.every((l) => /^\s*[-•]\s/.test(l))) {
    return <ul>{lines.map((l, i) => <li key={i}>{inline(l.replace(/^\s*[-•]\s/, ''), onNavigate)}</li>)}</ul>;
  }
  return lines.map((l, i) => <span key={i}>{i > 0 && <br />}{inline(l, onNavigate)}</span>);
}
function inline(s: string, onNavigate?: () => void) {
  const parts = s.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\((?:\/|https?:\/\/)[^)\s]+\))/g);
  return parts.map((x, i) => {
    if (x.startsWith('**') && x.endsWith('**')) return <strong key={i}>{x.slice(2, -2)}</strong>;
    const m = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(x);
    if (m) {
      return m[2].startsWith('/')
        ? <Link key={i} to={m[2]} className="asst__link" onClick={onNavigate}>{m[1]}</Link>
        : <a key={i} href={m[2]} className="asst__link" target="_blank" rel="noreferrer">{m[1]}</a>;
    }
    return <span key={i}>{x}</span>;
  });
}
